import {
    TableRow,
    Checkbox,
    Box,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { DragIndicator } from '@mui/icons-material';
import { SortableRankingTable, SortableRankingTableColumn } from 'src/components/common/DesignConceptRankingTable/SortableRankingTable';
import { RankingRow } from './RankingRow';
import { AddNewRankingRow } from './AddNewRankingRow';
import { EmptyRankingState } from './EmptyRankingState';

interface RankingTableProps {
    onRowSelect: (rankingIndex: number, selected: boolean) => void;
    rankings: Models.IngestConfigApi.ForcedRankings.Ranking[];
    selectedRows: number[];
    filteringPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    defaultPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    onFilteringOptionChange: (rankingIndex: number, specId: string | null) => void;
    onDefaultOptionChange: (rankingIndex: number, specId: string | null) => void;
    onImportCsv: (csvContent: string) => Promise<void>;
    onAddRanking: (ranking: Models.IngestConfigApi.ForcedRankings.Ranking) => void;
    onDeleteRanking: (rankingIndex: number) => void;
    showAddRow: boolean;
    onShowAddRow: () => void;
    onHideAddRow: () => void;
    onReorderRankings: (oldIndex: number, newIndex: number) => void;
    onSelectAll: (selected: boolean) => void;
    productKey: string;
    cultures: string[];
    hasCultureSelected: boolean;
    duplicateRankingsMap: Map<number, number[]>;
}

const AddNewRankingTableRow = styled(TableRow)(() => ({
    backgroundColor: '#e3f2fd', // Light blue for the add new ranking row
}));

const StyledBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(8),
    textAlign: 'left',
}));

export const RankingTable = ({
    onRowSelect,
    rankings,
    selectedRows,
    filteringPartialProductSpecs,
    defaultPartialProductSpecs,
    onFilteringOptionChange,
    onDefaultOptionChange,
    onImportCsv,
    onAddRanking,
    onDeleteRanking,
    showAddRow,
    onShowAddRow,
    onHideAddRow,
    onReorderRankings,
    onSelectAll,
    productKey,
    cultures,
    hasCultureSelected,
    duplicateRankingsMap,
}: RankingTableProps): JSX.Element => {
    const filteringOptionGroupOptions = useMemo(
        () => Object.entries(filteringPartialProductSpecs)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name)),
        [filteringPartialProductSpecs],
    );

    const defaultOptionGroupOptions = useMemo(
        () => Object.entries(defaultPartialProductSpecs)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name)),
        [defaultPartialProductSpecs],
    );

    const handleAddRanking = (ranking: Models.IngestConfigApi.ForcedRankings.Ranking): void => {
        onAddRanking(ranking);
        onHideAddRow();
    };

    const allSelected = rankings.length > 0 && selectedRows.length === rankings.length;
    const someSelected = selectedRows.length > 0 && selectedRows.length < rankings.length;

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        onSelectAll(event.target.checked);
    };

    const columns: SortableRankingTableColumn[] = [
        {
            key: 'selection',
            label: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Select multiple rows to change their filtering or default option groups at once">
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleSelectAllChange}
                        />
                    </Tooltip>
                    <StyledBox>
                        {selectedRows.length > 0 && (
                            <Typography color="text.secondary" variant="caption">
                                (
                                {selectedRows.length}
                                )
                            </Typography>
                        )}
                    </StyledBox>
                </Box>
            ),
            padding: 'none',
        },
        { key: 'rank', label: 'Rank' },
        { key: 'preview', label: 'Preview' },
        { key: 'conceptId', label: 'Design Concept ID' },
        { key: 'filtering', label: 'Filtering Option Group' },
        { key: 'default', label: 'Default Option Group' },
        { key: 'action', label: 'Action', align: 'right' },
    ];

    return (
        <SortableRankingTable
            columns={columns}
            dragHandleHeader={<DragIndicator color="disabled" />}
            emptyState={!showAddRow ? (
                <EmptyRankingState
                    hasCultureSelected={hasCultureSelected}
                    onImportCsv={onImportCsv}
                    onShowAddRow={onShowAddRow}
                />
            ) : undefined}
            getItemId={(_ranking, index): string => index.toString()}
            getItemKey={(ranking): string => `${ranking.designConceptId}-${ranking.filteringProductSpecId || 'no-filter'}-${ranking.defaultProductSpecId || 'no-default'}-${ranking.rank}`}
            getRowHighlighted={(_ranking, index): boolean => !!duplicateRankingsMap.get(index)}
            items={rankings}
            preBodyRows={showAddRow ? (
                <AddNewRankingTableRow>
                    <AddNewRankingRow
                        cultures={cultures}
                        currentRankedConceptIds={rankings.map((r) => r.designConceptId)}
                        defaultOptionGroupOptions={defaultOptionGroupOptions}
                        filteringOptionGroupOptions={filteringOptionGroupOptions}
                        productKey={productKey}
                        onAdd={handleAddRanking}
                        onHide={onHideAddRow}
                    />
                </AddNewRankingTableRow>
            ) : undefined}
            renderRow={(ranking, index): JSX.Element => {
                const duplicateRanks = duplicateRankingsMap.get(index);

                return (
                    <RankingRow
                        conceptId={ranking.designConceptId}
                        cultures={cultures}
                        defaultOptionGroup={ranking.defaultProductSpecId}
                        defaultOptionGroupOptions={defaultOptionGroupOptions}
                        duplicateRanks={duplicateRanks}
                        filteringOptionGroup={ranking.filteringProductSpecId}
                        filteringOptionGroupOptions={filteringOptionGroupOptions}
                        productKey={productKey}
                        rank={ranking.rank}
                        selected={selectedRows.includes(index)}
                        onDefaultOptionChange={(value): void => onDefaultOptionChange(index, value)}
                        onDelete={(): void => onDeleteRanking(index)}
                        onFilteringOptionChange={(value): void => onFilteringOptionChange(index, value)}
                        onSelect={(selected): void => onRowSelect(index, selected)}
                    />
                );
            }}
            tableSize="medium"
            onReorder={onReorderRankings}
        />
    );
};
